// PRO Drift Auditor -- rebalancing efficiency frontier.
//
// Dual-axis ComposedChart: friction cost ($) as bars on the right axis, with
// CAGR and Sharpe as lines on the left. The max-Sharpe corridor is highlighted
// with a reference line + callout so the optimal rebalance band is obvious.

import React, { useMemo } from 'react';
import {
    Bar,
    CartesianGrid,
    Cell,
    ComposedChart,
    Legend,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { GitBranch } from 'lucide-react';

import { Card } from './Card';
import { fmtMoney } from '../format';
import type { CorridorFrontierPoint } from '../types';

export const CorridorFrontierChart: React.FC<{ data: CorridorFrontierPoint[] }> = ({ data }) => {
    const optimal = useMemo(() => data.find((d) => d.is_optimal_sharpe), [data]);
    const chartData = useMemo(
        () => data.map((d) => ({ ...d, bucket: `±${d.corridor_threshold_pct}%` })),
        [data],
    );
    const optimalBucket = optimal ? `±${optimal.corridor_threshold_pct}%` : undefined;

    return (
        <Card
            title="Rebalancing Efficiency Frontier"
            subtitle="Corridor width vs net CAGR, Sharpe and total fee/tax friction. The marked band maximises Sharpe."
            icon={<GitBranch className="h-4 w-4" />}
            right={
                optimal && (
                    <div className="hidden sm:block rounded-xl bg-amber-50 dark:bg-amber-500/15 px-3 py-2 text-right">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-300">
                            Optimal Band
                        </div>
                        <div className="text-lg font-black tabular-nums text-amber-700 dark:text-amber-200">
                            ±{optimal.corridor_threshold_pct}%
                        </div>
                        <div className="text-[10px] text-amber-600/80 dark:text-amber-300/80">
                            Sharpe {optimal.sharpe_ratio.toFixed(2)}
                        </div>
                    </div>
                )
            }
        >
            <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 6, right: 10, left: 6, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.4} vertical={false} />
                        <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} />
                        <YAxis
                            yAxisId="left"
                            tick={{ fontSize: 11, fill: '#94a3b8' }}
                            tickLine={false}
                            axisLine={false}
                            width={40}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tickFormatter={(v: number) => fmtMoney(v)}
                            tick={{ fontSize: 11, fill: '#94a3b8' }}
                            tickLine={false}
                            axisLine={false}
                            width={60}
                        />
                        <Tooltip
                            formatter={(v: number, name) => {
                                if (name === 'Friction ($)') return [fmtMoney(v), name];
                                if (name === 'CAGR') return [`${v.toFixed(2)}%`, name];
                                return [v.toFixed(2), name];
                            }}
                            contentStyle={{
                                borderRadius: 12,
                                border: '1px solid #e2e8f0',
                                fontSize: 12,
                                background: 'rgba(255,255,255,0.97)',
                            }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {optimalBucket && (
                            <ReferenceLine
                                yAxisId="left"
                                x={optimalBucket}
                                stroke="#f59e0b"
                                strokeDasharray="4 3"
                                strokeWidth={1.5}
                            />
                        )}
                        <Bar yAxisId="right" dataKey="total_friction_cost_dollars" name="Friction ($)" radius={[4, 4, 0, 0]} barSize={26}>
                            {chartData.map((d, i) => (
                                <Cell key={i} fill={d.is_optimal_sharpe ? '#fbbf24' : '#cbd5e1'} />
                            ))}
                        </Bar>
                        <Line yAxisId="left" type="monotone" dataKey="cagr_pct" name="CAGR" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} />
                        <Line yAxisId="left" type="monotone" dataKey="sharpe_ratio" name="Sharpe" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3 }} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
};
